import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, useTheme } from "@mui/material";
import * as Sentry from "@sentry/react";
import { Button, PageHeader, SectionHeader } from "@components/ui_v2";
import { CardResumenCotizacion, CardVehiculoCotizador } from "@components/ui_v2/cotizadoresComponents";
import { calcularRentaArrendamientoPuro, formatCurrencyWhole } from "@helpers/funciones";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { TaxSystemConfigResponse } from "@interfaces/index";
import { PATHS_TOFU_V2 } from "@helpers/constantes";
import { TAX_SYSTEMS_CODES } from "@helpers/taxSystemsCodes";
import imgDolphinBlack from "./byd-dolphin-black.webp";
import imgDolphinBlue from "./byd-dolphin-blue.webp";

const TAX_SYSTEM_CODE = TAX_SYSTEMS_CODES.WUWEI_PF;

const IVA_PCT = 16;
const SEMANAS_POR_MES = 52 / 12;

// TODO: datos del prototipo (`WW_AUTOS` de `wuwei-screens.jsx`) y reglas de un arrendamiento puro tipo BYD mientras
// no existan el catálogo de unidades ni las reglas de WuWei. Al tenerlos, solo cambia esta constante.
const AUTO = {
  marca: "BYD",
  modelo: "Dolphin Mini Plus",
  anio: 2026,
  tipo: "Eléctrico",
  autonomia: "380 km",
  /** Precio de lista, IVA incluido. */
  precio: 415800,
  plazoMeses: 36,
  /** % anual sin IVA. */
  tasaAnual: 19.51,
  /** % del valor sin IVA; es la opción a compra al final del contrato. */
  residualPct: 20,
  // Servicios incluidos en la renta, con IVA.
  /** Seguro todo riesgo de uso en plataforma: % anual del precio. */
  seguroAnualPct: 10,
  /** Mantenimiento preventivo de un eléctrico a ~60,000 km al año, MXN / año. */
  mantenimientoAnual: 18000,
  /** GPS y telemetría, MXN / mes. */
  gpsMensual: 800,
  /** Gestión vehicular (placas, verificación, trámites): % anual del precio. */
  administracionAnualPct: 4.5,
  colores: [
    // TODO: cambiar por la foto blanca (`byd-dolphin-white.webp`); mientras, se usa la azul.
    { id: "blanco", nombre: "Apricity White", hex: "#E9E3D2", img: imgDolphinBlue },
    { id: "negro", nombre: "Polar Night Black", hex: "#1C1F23", img: imgDolphinBlack },
    { id: "azul", nombre: "Azul Glaciar", hex: "#AEC6DD", img: imgDolphinBlue },
  ],
};

/**
 * Renta semanal de arrendamiento puro sin enganche: el financiamiento es el PMT semanal del valor sin IVA con el
 * residual como valor final, y encima van los servicios y la administración, de su costo anual o mensual a semanas.
 * La renta en garantía es un mes de financiamiento y se devuelve al final del contrato.
 */
const financiamiento = Math.round(
  calcularRentaArrendamientoPuro({
    valor: AUTO.precio / (1 + IVA_PCT / 100),
    enganchePct: 0,
    plazo: Math.round(AUTO.plazoMeses * SEMANAS_POR_MES),
    tasaAnual: AUTO.tasaAnual,
    residualPct: AUTO.residualPct,
    ivaPct: IVA_PCT,
    diasPeriodo: 7,
  }).rentaConIva,
);
const servicios = Math.round(
  ((AUTO.precio * AUTO.seguroAnualPct) / 100 + AUTO.mantenimientoAnual) / 52 + AUTO.gpsMensual / SEMANAS_POR_MES,
);
const administracion = Math.round((AUTO.precio * AUTO.administracionAnualPct) / 100 / 52);
const RENTA = {
  semanal: financiamiento + servicios + administracion,
  garantia: Math.round(financiamiento * SEMANAS_POR_MES),
  desglose: [
    { label: "Financiamiento", value: financiamiento },
    { label: "Seguro + Mant. + GPS", value: servicios },
    { label: "Administración", value: administracion },
  ],
};

/**
 * Cotizador de WuWei (`WW_Cotizador` del diseño): arrendamiento de auto para conductores de plataforma. No hay monto
 * ni plazo que ajustar: se elige el color de la unidad y se muestra la renta semanal con su desglose.
 */
const CotizadorWuWei = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [config, setConfig] = useState<TaxSystemConfigResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [colorId, setColorId] = useState(AUTO.colores[0].id);

  const getInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      setConfig(await getTaxSystemConfig(TAX_SYSTEM_CODE).unwrap());
    } catch (error) {
      // Mismo alcance que los otros cotizadores: sin config no se puede continuar, sin modal de error.
      Sentry.captureException(error, { extra: { component: "CotizadorWuWei-initial_fetch" } });
    } finally {
      setIsLoading(false);
    }
  }, [getTaxSystemConfig]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  // Igual que los otros cotizadores: el código de origen de la liga se guarda para la creación de cuenta.
  useEffect(() => {
    const sourceCode = new URLSearchParams(window.location.search).get("code");
    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, []);

  // Mismos parámetros que los otros cotizadores, con el precio de la unidad como monto y su plazo en meses; la
  // cuenta se crea con el celular (`account_type=phone`).
  // TODO: mandar también la unidad y el color elegidos cuando el backend los reciba.
  const handleSolicitar = () => {
    if (!config?.product_id) return;
    const sourceCode = sessionStorage.getItem("source_code");
    let urlPortal = `${PATHS_TOFU_V2.crearCuenta.path}?tax_system_id=${config.id}&product_id=${config.product_id}&person_type=${config.code}&loan_amount=${AUTO.precio}&loan_term=${AUTO.plazoMeses}&tax_system_type=${config.tax_system_type}&account_type=phone`;
    if (sourceCode) {
      urlPortal += `&source_code=${sourceCode}`;
    }
    navigate(urlPortal);
  };

  return (
    <Box>
      <PageHeader
        variant="cotizador"
        eyebrow="Cotización · Arrendamiento de auto"
        title="Maneja tu propio auto"
        subtitle="Elige el color de tu unidad y ajusta tu plazo. Al final del contrato, el auto puede ser tuyo con la opción a compra."
      />

      <SectionHeader
        variant="underline"
        label="Tu unidad"
        meta="Nueva · 100% eléctrica · seguro, mantenimiento y GPS incluidos"
      />

      <CardVehiculoCotizador
        nombre={`${AUTO.marca} ${AUTO.modelo}`}
        meta={`${AUTO.anio} · ${AUTO.tipo} · ${AUTO.autonomia} de autonomía`}
        colores={AUTO.colores}
        value={colorId}
        onChange={setColorId}
        testId="cotizador-wuwei-color"
      />

      <CardResumenCotizacion
        label="Pago semanal"
        // La tarjeta va en el dorado de la marca (`accent`), no en el azul marino del botón.
        brandColor={theme.palette.primary.accent}
        amount={formatCurrencyWhole(RENTA.semanal)}
        amountFontSize={30}
        caption={`MXN / semana (IVA incluido) durante ${AUTO.plazoMeses} meses · con opción a compra al final`}
        disclaimer={`Todo incluido: seguro todo riesgo, mantenimiento preventivo, GPS y gestión vehicular. Sin enganche, únicamente una renta en garantía de ${formatCurrencyWhole(RENTA.garantia)} que se te devuelve al final del contrato. La renta se cobra por descuento automático de tus ganancias en la plataforma. Oferta final sujeta a aprobación.`}
        actions={
          <Button
            tone="hero"
            withArrow
            onClick={handleSolicitar}
            disabled={isLoading || !config?.product_id}
            data-testid="cotizador-wuwei-continue-button"
          >
            Solicitar mi auto
          </Button>
        }
      >
        {/* Desglose de la renta semanal (`quote-hero-main` del diseño). */}
        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: "5px", maxWidth: 320 }}>
          {RENTA.desglose.map((item) => (
            <Box key={item.label} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
              <Typography sx={{ fontSize: 12.5, color: "tokens.textTertiary" }}>{item.label}</Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "tokens.textPrimary" }}>
                {formatCurrencyWhole(item.value)}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardResumenCotizacion>
    </Box>
  );
};

export default CotizadorWuWei;
